import { createInstance, type i18n as I18n } from "i18next";
import React, { useEffect } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";

import { type Locale, LOCALES } from "@/5-shared/types/common";
import { setActiveLocale } from "@/5-shared/utils/activeLocale";
import { defaultNS,resources } from "@/locales/resources";

import { queryClient } from "./QueryProvider";

type Props = {
  children: React.ReactNode;
  locale?: string;
  fallbackLocale?: string;
};

const LOCALE_DEFAULT = "it";

const allNamespaces = Object.keys(resources.it);

function makeI18n(lng: string, fallbackLng: string): I18n {
  const i18n = createInstance();
  void i18n.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng,
    ns: allNamespaces,
    defaultNS,
    interpolation: { escapeValue: false },
    react: {
      useSuspense: false,
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: [
        "br",
        "strong",
        "em",
        "b",
        "u",
        "span",
        "ul",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
      ],
    },
  });
  return i18n;
}

const isBrowser = typeof window !== "undefined";
let clientI18n: I18n | null = null;


export { LOCALE_DEFAULT, LOCALES };

export type { Locale };

export function isLocale(s: string): s is Locale {
  return (LOCALES as readonly string[]).includes(s);
}

export default function I18nProvider({
  children,
  locale = "it",
  fallbackLocale = "it",
}: Props) {
  const i18n = isBrowser
    ? (clientI18n ??= makeI18n(locale, fallbackLocale))
    : makeI18n(locale, fallbackLocale);

  // Mirror the locale in render (before any child effect fires a request), so the first
  // fetch already carries the right `Accept-Language` and Zod speaks the same language.
  setActiveLocale(isLocale(locale) ? locale : LOCALE_DEFAULT);

  useEffect(() => {
    // Only on an actual locale switch (never the initial mount, where i18n already
    // holds `locale`): retranslate, and refetch every locale-dependent server query
    // (blog, plants, ...) so it re-requests under the new `Accept-Language`. The
    // render-phase setActiveLocale above has already updated the header value.
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
      void queryClient.invalidateQueries();
    }
  }, [i18n, locale]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
